import axios from "axios";

// Prefer the page's own origin when it's the same site as the configured backend
// (www vs. non-www), so cookies + CORS always line up.
const resolveBackendUrl = () => {
  const configured = process.env.REACT_APP_BACKEND_URL;
  try {
    const env = new URL(configured);
    const strip = (h) => h.replace(/^www\./, "");
    if (strip(env.hostname) === strip(window.location.hostname) && env.origin !== window.location.origin) {
      return window.location.origin;
    }
  } catch {
    /* fall through */
  }
  return configured;
};

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach Bearer token (fallback to cookie) from localStorage if present.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("crm_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth: password recovery
export async function forgotPassword(email) {
  const { data } = await api.post("/auth/forgot-password", { email });
  return data;
}

export async function resetPassword(token, new_password) {
  const { data } = await api.post("/auth/reset-password", { token, new_password });
  return data;
}

export async function changePassword(current_password, new_password) {
  const { data } = await api.post("/auth/change-password", { current_password, new_password });
  return data;
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Public site endpoints
export function trackClick(link_id, label, page) {
  const body = JSON.stringify({ link_id, label, page: page || window.location.pathname });
  try {
    fetch(`${API}/analytics/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* fire-and-forget */
  }
}

export async function submitLead(payload) {
  const { data } = await api.post("/leads", payload);
  return data;
}

export async function submitQuote(payload) {
  const { data } = await api.post("/quotes", payload);
  return data;
}

// Auth
export async function login(email, password) {
  const { data } = await api.post("/auth/login", { email, password });
  if (data?.access_token) localStorage.setItem("crm_token", data.access_token);
  return data;
}

export async function logout() {
  try {
    await api.post("/auth/logout");
  } finally {
    localStorage.removeItem("crm_token");
  }
}

export async function fetchMe() {
  const { data } = await api.get("/auth/me");
  return data;
}

// CRM
export async function listClients(params = {}) {
  const { data } = await api.get("/admin/clients", { params });
  return data;
}

export async function getClient(id) {
  const { data } = await api.get(`/admin/clients/${id}`);
  return data;
}

export async function createClient(payload) {
  const { data } = await api.post("/admin/clients", payload);
  return data;
}

export async function updateClient(id, payload) {
  const { data } = await api.put(`/admin/clients/${id}`, payload);
  return data;
}

export async function deleteClient(id) {
  await api.delete(`/admin/clients/${id}`);
}

export async function updateClientStatus(id, lead_status) {
  const { data } = await api.patch(`/admin/clients/${id}/status`, { lead_status });
  return data;
}

export async function fetchAnalytics() {
  const { data } = await api.get("/admin/analytics");
  return data;
}

export async function fetchClickAnalytics() {
  const { data } = await api.get("/admin/analytics/clicks");
  return data;
}

// Dentist network checker
export async function checkDentist(payload) {
  const { data } = await api.post("/dentists/check", payload);
  return data;
}

export async function listDentists(q) {
  const { data } = await api.get("/admin/dentists", { params: q ? { q } : undefined });
  return data;
}

export async function createDentist(payload) {
  const { data } = await api.post("/admin/dentists", payload);
  return data;
}

export async function updateDentist(id, payload) {
  const { data } = await api.put(`/admin/dentists/${id}`, payload);
  return data;
}

export async function deleteDentist(id) {
  await api.delete(`/admin/dentists/${id}`);
}

export async function npiLookup(params) {
  const { data } = await api.get("/admin/dentists/npi-lookup", { params });
  return data;
}

export async function importDentistsCsv(csv, dry_run, update_existing) {
  const { data } = await api.post("/admin/dentists/import", { csv, dry_run, update_existing });
  return data;
}

export async function downloadDentistCsv(kind) {
  const { data } = await api.get(`/admin/dentists/${kind}.csv`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = kind === "template" ? "dentists-template.csv" : "dentists-export.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function fetchDigestStatus() {
  const { data } = await api.get("/admin/digest/status");
  return data;
}

export async function sendDigestNow() {
  const { data } = await api.post("/admin/digest/send");
  return data;
}

export async function fetchDentistSearches(days) {
  const { data } = await api.get("/admin/dentists/searches", { params: { days: days || 0 } });
  return data;
}

export async function fetchSlideAnalytics(days) {
  const params = days && days > 0 ? { days } : undefined;
  const { data } = await api.get("/admin/analytics/slides", { params });
  return data;
}
